/**
 * State and derived values for ChatInputActions.
 *
 * Each hook is called unconditionally from ChatInputActions, in the order the
 * component has always declared its state and effects, so the effects still run
 * in the same order: the skills fetch, then the add-menu signal, then the add
 * menu's keyboard listener.
 */
import type { NavigateFunction } from "react-router-dom";
import type { SkillCatalogEntry } from "@/types/skills";
import type { AuthenticatedFetchFn } from "@/providers/WeBuildClientProvider";
import type { SkillCommandEntry } from "./ChatCommandMenu";

import React from "react";

import { logger } from "@/telemetry";
import { blurActiveElement } from "@/utils/dom";
import { siteConfig } from "@/config/site";

/** A trailing slash on the admin API base URL, stripped before a path is appended. */
const TRAILING_SLASH = /\/$/;

/** The add menu's action rows that ArrowUp/Down move between (toggle rows excluded). */
const ADD_MENU_NAV_SELECTOR = "[data-add-menu-nav]:not([disabled])";

// ── Real skills catalogue for the command menu's Skills section ────────
/**
 * Fetched once the command menu first opens (server already filters by the
 * user's access). Only active or global skills are surfaced — the assistant
 * cannot use a disabled tenant skill.
 */
export const useSkillsCatalogue = (
  commandMenuVisible: boolean,
  authenticatedFetch: AuthenticatedFetchFn | null,
): SkillCommandEntry[] => {
  const [skills, setSkills] = React.useState<SkillCommandEntry[]>([]);
  const skillsFetchedRef = React.useRef(false);

  React.useEffect(() => {
    if (!commandMenuVisible || skillsFetchedRef.current || !authenticatedFetch) return;
    skillsFetchedRef.current = true;

    const baseUrl = siteConfig().adminAPIURL.replace(TRAILING_SLASH, "");

    authenticatedFetch(`${baseUrl}/admin/v1/skills/catalog`)
      .then(async (response) => {
        if (!response.ok) {
          logger.error("Failed to fetch skills catalogue", { status: response.status });

          return;
        }

        const data: SkillCatalogEntry[] = await response.json();

        setSkills(
          data
            .filter((s) => s.is_global || s.is_active)
            .map((s) => ({
              id: s.id,
              name: s.name,
              description: s.description,
            })),
        );
      })
      .catch((error) => {
        // Allow a later open to retry if the request failed outright.
        skillsFetchedRef.current = false;
        logger.error("Error fetching skills catalogue", { error: String(error) });
      });
  }, [commandMenuVisible, authenticatedFetch]);

  return skills;
};

/** Which panel the add menu is showing: the root list or the connectors sub-view. */
export type AddMenuView = "main" | "connectors";

interface AddMenuOptions {
  openAddMenuSignal?: number;
  navigate: NavigateFunction;
  prefetchStatuses: () => void;
  ensureToolConnected: (serviceId: string) => void;
  onFileUploadClick: () => void;
  onMention: () => void;
}

/** The "+" add menu's open state, current view and every action it offers. */
export interface AddMenuState {
  isOpen: boolean;
  view: AddMenuView;
  /** Reset to the root view whenever the add menu closes. */
  onOpenChange: (open: boolean) => void;
  /** Return from the connectors sub-view to the root list. */
  onShowMain: () => void;
  onAttachFile: () => void;
  onMention: () => void;
  onOpenConnectors: () => void;
  onConnectorPress: (serviceId: string) => void;
  onManageConnectors: () => void;
  onOpenChatInputSettings: () => void;
}

/**
 * State and handlers for the "+" add menu. The state lives here rather than in
 * the menu component, so it is held for the composer's lifetime whether or not
 * file upload is permitted, as it always has been.
 */
export const useAddMenu = ({
  openAddMenuSignal,
  navigate,
  prefetchStatuses,
  ensureToolConnected,
  onFileUploadClick,
  onMention,
}: AddMenuOptions): AddMenuState => {
  // ── Popover open state for the "+" add menu ──────────────────────────
  const [addMenuOpen, setAddMenuOpen] = React.useState(false);
  // Which panel the add menu is showing: the root list or the connectors sub-view.
  const [addMenuView, setAddMenuView] = React.useState<AddMenuView>("main");

  // Open the add menu (root view) when the parent signals it — e.g. the user
  // typed "+" in the composer, the keyboard equivalent of clicking the + button.
  React.useEffect(() => {
    if (openAddMenuSignal) {
      setAddMenuView("main");
      setAddMenuOpen(true);
    }
  }, [openAddMenuSignal]);

  /** Open the connectors sub-view and warm the integration status cache. */
  const handleOpenConnectors = () => {
    logger.info("ChatInputActions: connectors view opened");
    setAddMenuView("connectors");
    prefetchStatuses();
  };

  /** Connect a connector via the shared OAuth popup flow (reused from ToolPicker). */
  const handleConnectorPress = (serviceId: string) => {
    logger.info("ChatInputActions: connector connect pressed", { serviceId });
    ensureToolConnected(serviceId);
  };

  /** Reset to the root view whenever the add menu closes. */
  const handleAddMenuOpenChange = (open: boolean) => {
    setAddMenuOpen(open);
    if (!open) setAddMenuView("main");
  };

  /** "Manage connectors" navigates to the integrations settings page. */
  const handleManageConnectors = () => {
    setAddMenuOpen(false);
    setAddMenuView("main");
    navigate("/settings#integrations");
  };

  /** Handle "Attach file" press: trigger the hidden file input and close the menu. */
  const handleAttachFile = () => {
    logger.info("ChatInputActions: attach file pressed");
    setAddMenuOpen(false);
    // Small delay so the popover has time to dismiss before the file dialog opens.
    setTimeout(() => {
      onFileUploadClick();
      blurActiveElement();
    }, 0);
  };

  /** "Mention people or workflows" closes the add menu then delegates to the parent handler. */
  const handleMention = () => {
    logger.info("ChatInputActions: mention pressed");
    setAddMenuOpen(false);
    onMention();
  };

  /** Open chat-input settings from the add-menu footer cog. */
  const handleOpenChatInputSettings = () => {
    setAddMenuOpen(false);
    setAddMenuView("main");
    navigate("/settings#general");
  };

  return {
    isOpen: addMenuOpen,
    view: addMenuView,
    onOpenChange: handleAddMenuOpenChange,
    onShowMain: () => setAddMenuView("main"),
    onAttachFile: handleAttachFile,
    onMention: handleMention,
    onOpenConnectors: handleOpenConnectors,
    onConnectorPress: handleConnectorPress,
    onManageConnectors: handleManageConnectors,
    onOpenChatInputSettings: handleOpenChatInputSettings,
  };
};

/**
 * ArrowUp/Down roving focus across the add menu's action rows so the footer's
 * "Navigate" hint is accurate. A document-level listener (only while the menu
 * is open) is used because HeroUI's PopoverContent does not forward onKeyDown
 * and focus may sit on the dialog container rather than on an item.
 */
export const useAddMenuKeyboardNav = (isOpen: boolean) => {
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;

      const items = Array.from(document.querySelectorAll<HTMLButtonElement>(ADD_MENU_NAV_SELECTOR));

      if (items.length === 0) return;
      e.preventDefault();

      const currentIndex = items.findIndex((el) => el === document.activeElement);
      const delta = e.key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        currentIndex === -1
          ? e.key === "ArrowDown"
            ? 0
            : items.length - 1
          : (currentIndex + delta + items.length) % items.length;

      items[nextIndex]?.focus();
    };

    // Capture phase so the handler runs even if HeroUI's popover stops
    // propagation of the key event before it bubbles to the document.
    document.addEventListener("keydown", handleKeyDown, true);

    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen]);
};
